import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/buttons/Button';
import { Card } from '../../components/cards/Card';
import {
  Search, Eye, ShieldCheck, UserCheck, UploadCloud,
  BarChart3, CreditCard, Home, MessageSquare, CheckCircle,
  ChevronDown, ChevronUp, AlertCircle, FileText
} from 'lucide-react';

import { SEOHead } from '../../components/seo/SEOHead';
import { Breadcrumb } from '../../components/seo/Breadcrumb';

type RoleTab = 'students' | 'agents' | 'landlords';

export const HowItWorks: React.FC = () => {
  const [activeTab, setActiveTab] = useState<RoleTab>('students');
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const content = {
    students: {
      overview: "Find your ideal home near campus without the hassle. Secure, verified, and easy.",
      steps: [
        {
          title: "Browse & Search",
          description: "Enter your university, city, or area. Filter by price, room type, and amenities to find matches.",
          icon: Search
        },
        {
          title: "View & Connect",
          description: "Check detailed photos, map locations, and reviews. Message the host directly to ask questions.",
          icon: Eye
        },
        {
          title: "Book Securely",
          description: "Select your room and request a booking. Payments are protected until your move-in is confirmed.",
          icon: ShieldCheck
        }
      ],
      features: [
        { icon: CheckCircle, label: "Verified Listings" },
        { icon: MessageSquare, label: "Direct Messaging" },
        { icon: CreditCard, label: "Secure Payments" },
        { icon: FileText, label: "Online Leases" }
      ]
    },
    agents: {
      overview: "Manage your portfolio, track leads, and reach thousands of students efficiently.",
      steps: [
        {
          title: "Register Account",
          description: "Sign up as an agent. You'll need to provide business details for verification.",
          icon: UserCheck
        },
        {
          title: "Get Verified",
          description: "Our admin team reviews your credentials. Once approved, you gain full access to listing tools.",
          icon: ShieldCheck
        },
        {
          title: "List & Manage",
          description: "Upload properties, manage availability, and respond to student inquiries in real-time.",
          icon: UploadCloud
        }
      ],
      features: [
        { icon: BarChart3, label: "Lead Analytics" },
        { icon: Home, label: "Portfolio Management" },
        { icon: MessageSquare, label: "Centralized Inbox" },
        { icon: CheckCircle, label: "Brand Visibility" }
      ]
    },
    landlords: {
      overview: "Fill your rooms faster with guaranteed rent payments and tenant screening.",
      steps: [
        {
          title: "Create Profile",
          description: "Register as a landlord. Automatically join our Early Access plan to list for free.",
          icon: UserCheck
        },
        {
          title: "List Property",
          description: "Add photos, pricing, and details. Your listing goes live after a quick quality check.",
          icon: UploadCloud
        },
        {
          title: "Screen & Rent",
          description: "Receive booking requests, screen tenant profiles, and accept leases online.",
          icon: CheckCircle
        }
      ],
      features: [
        { icon: ShieldCheck, label: "Tenant Screening" },
        { icon: CreditCard, label: "Automated Billing" },
        { icon: Eye, label: "High Visibility" },
        { icon: FileText, label: "Digital Contracts" }
      ]
    }
  };

  const faqs = [
    { q: "Is Indigo Beans Rooms free to use?", a: "For students, Indigo Beans Rooms is completely free. Agents and landlords can currently list up to 3 properties for free during Early Access." },
    { q: "How does verification work?", a: "We check ID documents and business registrations for agents and landlords to ensure student safety." },
    { q: "Are payments secure?", a: "Yes, we use an escrow-like system. Funds are held securely until the booking is confirmed." },
    { q: "Can I cancel a booking?", a: "Cancellation policies vary by property. Check the specific listing details for cancellation terms." }
  ];

  return (
    <>
      <SEOHead
        title="How It Works - Renting Made Easy | IndigoBeans Rooms"
        description="Learn how to find and book your perfect room with IndigoBeans. Simple steps for students, agents, and landlords. Secure, verified, and hassle-free."
        canonical="https://rooms.indigobeans.co.za/how-it-works"
      />
      <div className="min-h-screen bg-white font-sans">
        {/* Breadcrumb */}
        <div className="bg-white border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-4 py-2">
            <Breadcrumb items={[{ name: 'How It Works', url: '/how-it-works' }]} />
          </div>
        </div>

        {/* 2. Page Header / Intro Section */}
        <section className="relative py-24 text-center text-white overflow-hidden">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=1950&q=80')] bg-cover bg-center"></div>
          <div className="absolute inset-0 bg-[#2f5c96]/90 mix-blend-multiply"></div>
          <div className="relative z-10 max-w-4xl mx-auto px-4">
            <h1 className="text-4xl lg:text-5xl font-extrabold tracking-tight mb-4 text-white">
              How <span className="text-blue-200">Indigo Beans Rooms</span> Works
            </h1>
            <p className="text-xl text-blue-100 max-w-2xl mx-auto">
              Whether you're studying, renting, or managing, we make accommodation simple.
            </p>
          </div>
        </section>

        {/* 3. Role Selector Section */}
        <section className="sticky top-20 z-40 bg-white/95 backdrop-blur-sm border-b border-gray-100 shadow-sm">
          <div className="max-w-3xl mx-auto px-4">
            <div className="flex justify-center">
              {['students', 'agents', 'landlords'].map((role) => (
                <button
                  key={role}
                  onClick={() => setActiveTab(role as RoleTab)}
                  className={`flex-1 py-4 text-sm sm:text-base font-bold uppercase tracking-wide border-b-4 transition-colors ${activeTab === role
                    ? 'border-[#2f5c96] text-[#2f5c96]'
                    : 'border-transparent text-gray-400 hover:text-gray-600'
                    }`}
                >
                  For {role}
                </button>
              ))}
            </div>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 space-y-20">

          {/* 4. Role Overview Block */}
          <section className="text-center max-w-3xl mx-auto animate-fadeIn">
            <h2 className="text-3xl font-bold text-gray-900 mb-4 capitalize">
              {activeTab === 'students' ? 'Find Your Place' : (activeTab === 'agents' ? 'Grow Your Business' : 'Maximize Occupancy')}
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              {content[activeTab].overview}
            </p>
          </section>

          {/* 5. Step-by-Step Process Section */}
          <section>
            <div className="grid md:grid-cols-3 gap-8 relative">
              {/* Connector Line (Desktop) */}
              <div className="hidden md:block absolute top-8 left-1/6 right-1/6 h-0.5 bg-gray-100 -z-10"></div>

              {content[activeTab].steps.map((step, index) => (
                <div key={index} className="flex flex-col items-center text-center group">
                  <div className="w-16 h-16 bg-white border-2 border-[#2f5c96] rounded-full flex items-center justify-center mb-6 shadow-sm group-hover:bg-[#2f5c96] group-hover:text-white transition-colors duration-300 relative z-10">
                    <step.icon className="w-8 h-8" />
                    <div className="absolute -top-2 -right-2 w-6 h-6 bg-[#c19cca] text-white text-xs font-bold rounded-full flex items-center justify-center border-2 border-white">
                      {index + 1}
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
                  <p className="text-gray-600 px-4">{step.description}</p>
                </div>
              ))}
            </div>
          </section>

          {/* 6. What You Can Do Section */}
          <section>
            <h3 className="text-center text-sm font-bold text-gray-400 uppercase tracking-widest mb-10">
              Platform Features
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {content[activeTab].features.map((feature, i) => (
                <div key={i} className="bg-white border border-gray-100 p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow flex flex-col items-center text-center">
                  <feature.icon className="w-8 h-8 text-[#c19cca] mb-4" />
                  <span className="font-semibold text-gray-900">{feature.label}</span>
                </div>
              ))}
            </div>
          </section>

          {/* 7. Approval & Verification Section (Agents & Landlords) */}
          {(activeTab === 'agents' || activeTab === 'landlords') && (
            <section className="bg-blue-50 border border-blue-100 rounded-2xl p-8 lg:p-12">
              <div className="flex flex-col md:flex-row items-center gap-8">
                <div className="bg-white p-4 rounded-full shadow-sm text-blue-600">
                  <AlertCircle className="w-10 h-10" />
                </div>
                <div className="flex-1 text-center md:text-left">
                  <h3 className="text-xl font-bold text-blue-900 mb-2">Verification Required</h3>
                  <p className="text-blue-800/80">
                    To maintain a safe community, all {activeTab} accounts must be verified by our admin team before listings go live. This process typically takes 24-48 hours after you submit your documents.
                  </p>
                </div>
              </div>
            </section>
          )}

          {/* 8. Pricing Callout Section (Landlords) */}
          {activeTab === 'landlords' && (
            <section className="bg-slate-900 rounded-2xl p-8 lg:p-12 text-white overflow-hidden relative">
              <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
                <div>
                  <h3 className="text-2xl font-bold mb-2">Simple, Transparent Pricing</h3>
                  <p className="text-slate-300 max-w-lg">
                    Join our Early Access program today and list up to 3 properties completely free.
                  </p>
                </div>
                <Link to="/pricing">
                  <Button className="bg-[#c19cca] hover:bg-[#b08bb6] text-white border-none px-8 py-3">
                    View Early Access Plan
                  </Button>
                </Link>
              </div>
              {/* Background decoration */}
              <div className="absolute right-0 top-0 w-64 h-64 bg-[#2f5c96] opacity-20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3"></div>
            </section>
          )}

          {/* 9. Call-to-Action Section */}
          <section className="py-12 text-center">
            <div className="bg-[#2f5c96] rounded-3xl p-10 lg:p-16 shadow-xl relative overflow-hidden">
              <div className="relative z-10">
                <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
                  Ready to get started?
                </h2>
                <p className="text-blue-100 text-lg mb-10 max-w-2xl mx-auto">
                  Join thousands of students and property managers on Indigo Beans Rooms today.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/register">
                    <Button className="w-full sm:w-auto bg-white text-[#2f5c96] hover:bg-gray-100 text-lg px-8 py-4 shadow-lg border-none">
                      Create Free Account
                    </Button>
                  </Link>
                  {activeTab === 'students' && (
                    <Link to="/browse">
                      <Button variant="outline" className="w-full sm:w-auto border-white text-white hover:bg-white/10 text-lg px-8 py-4">
                        Browse Accommodation
                      </Button>
                    </Link>
                  )}
                </div>
              </div>
              {/* Background Pattern */}
              <div className="absolute top-0 left-0 w-full h-full opacity-10 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-white via-transparent to-transparent"></div>
            </div>
          </section>

          {/* 10. FAQ Preview Section */}
          <section className="max-w-3xl mx-auto">
            <h3 className="text-2xl font-bold text-gray-900 text-center mb-8">Frequently Asked Questions</h3>
            <div className="space-y-4">
              {faqs.map((faq, idx) => (
                <div key={idx} className="border border-gray-200 rounded-lg overflow-hidden">
                  <button
                    onClick={() => toggleFaq(idx)}
                    className="w-full flex items-center justify-between p-5 text-left bg-white hover:bg-gray-50 transition-colors"
                  >
                    <span className="font-semibold text-gray-800">{faq.q}</span>
                    {openFaq === idx ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                  </button>
                  {openFaq === idx && (
                    <div className="p-5 bg-gray-50 border-t border-gray-100 text-gray-600 text-sm leading-relaxed animate-fadeIn">
                      {faq.a}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>

        </div>
      </div>
    </>
  );
};